import React, { memo } from 'react';
import { Camera, ImageIcon, Send, Mic } from 'lucide-react';

interface ChatInputProps {
  inputValue: string;
  setInputValue: (value: string) => void;
  isProcessing: boolean;
  isRecording: boolean;
  recordingSecondsLeft?: number;
  handleSendMessage: (text: string) => void;
  handleMicClick: () => void;
  handleImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const ChatInput = memo(({
  inputValue,
  setInputValue,
  isProcessing,
  isRecording,
  recordingSecondsLeft = 60,
  handleSendMessage,
  handleMicClick,
  handleImageUpload
}: ChatInputProps) => {
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white p-3 border-t border-zinc-100 pb-safe z-50 lg:static lg:shadow-none lg:border-t-0 lg:p-0 lg:mt-auto">
      <div className="flex items-center gap-2 max-w-7xl mx-auto">
        <div className="flex gap-1">
          <label className="p-2 text-zinc-400 cursor-pointer hover:bg-zinc-100 rounded-full transition-colors">
            <Camera size={20} />
            <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
          </label>
          <label className="p-2 text-zinc-400 cursor-pointer hover:bg-zinc-100 rounded-full transition-colors">
            <ImageIcon size={20} />
            <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
          </label>
        </div>

        {isRecording ? (
          <div className="flex-1 bg-rose-50/80 border border-rose-200 py-2.5 px-4 rounded-2xl flex items-center justify-between text-xs animate-pulse">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-ping"></span>
              <span className="font-bold text-rose-700">Gravando áudio do serviço...</span>
            </div>
            <div className="font-mono font-black text-rose-600 bg-rose-100/80 px-2 py-0.5 rounded-lg border border-rose-300">
              00:{recordingSecondsLeft < 10 ? `0${recordingSecondsLeft}` : recordingSecondsLeft}
            </div>
          </div>
        ) : (
          <input 
            type="text" 
            value={inputValue} 
            onChange={e => setInputValue(e.target.value)} 
            onKeyDown={e => e.key === 'Enter' && handleSendMessage(inputValue)}
            placeholder="Descreva o orçamento ou suba uma foto..." 
            className="flex-1 bg-zinc-50 py-3 px-5 rounded-2xl text-sm outline-none border border-transparent focus:border-brand/30 shadow-inner"
          />
        )}

        <button 
          onClick={() => inputValue.trim() ? handleSendMessage(inputValue) : handleMicClick()}
          disabled={(isProcessing && !isRecording)}
          title={isRecording ? "Concluir e enviar áudio" : "Gravar áudio do serviço (máx 60s)"}
          className={`p-3.5 rounded-full shadow-md transition-all ${
            inputValue.trim() 
              ? 'bg-brand text-white hover:bg-brand-dark' 
              : isRecording 
              ? 'bg-rose-500 text-white hover:bg-rose-600 shadow-rose-200' 
              : 'bg-zinc-100 text-zinc-400 hover:bg-zinc-200'
          }`}
        >
          {inputValue.trim() ? (
            <Send size={18} className="translate-x-px" />
          ) : (
            <Mic size={18} className={isRecording ? 'animate-bounce text-white' : ''} />
          )}
        </button>
      </div>
    </div>
  );
});

ChatInput.displayName = 'ChatInput';

export default ChatInput;
